import { useState } from "react";
import { CreditCard } from "lucide-react";
import { useAppointments } from "@/features/appointments/hooks/appointmentQueries";
import { useClients } from "@/features/clients/hooks/clientQueries";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { getServiceName } from "@/features/services/utils/getServiceName";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { InitialsAvatar } from "@/shared/components/ui/InitialsAvatar";
import { ItemList } from "@/shared/components/ui/ItemList";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { StatusBadge, type StatusBadgeProps } from "@/shared/components/ui/StatusBadge";
import { Tabs } from "@/shared/components/ui/Tabs";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate } from "@/shared/utils/formatters";
import { useStripeTransactions } from "../hooks/stripeQueries";
import type { StripeTransaction, StripeTransactionFilter } from "../types/stripeTypes";
import { formatStripeMinorAmount } from "../utils/stripeMoney";

interface Props {
  businessId: string;
  userId: string;
}

const emptyMessages: Record<StripeTransactionFilter, string> = {
  all: "No Stripe payments yet.",
  refunded: "No refunded payments.",
  disputed: "No disputed payments.",
};

function getTransactionStatus(transaction: StripeTransaction): { label: string; tone: StatusBadgeProps["tone"] } {
  if (transaction.disputed) return { label: "Disputed", tone: "danger" };
  if (transaction.refunded) return { label: "Refunded", tone: "neutral" };
  if (transaction.amountRefundedMinor > 0) return { label: "Partly refunded", tone: "neutral" };
  if (transaction.status === "failed") return { label: "Failed", tone: "danger" };
  if (transaction.status === "pending") return { label: "Processing", tone: "pending" };
  return { label: "Paid", tone: "positive" };
}

const formatCardBrand = (brand: string) =>
  brand === "amex" ? "Amex" : brand.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

export function StripeTransactionList({ businessId, userId }: Props) {
  const [filter, setFilter] = useState<StripeTransactionFilter>("all");
  const transactions = useStripeTransactions(businessId, userId, filter);
  const appointments = useAppointments();
  const clients = useClients();
  const services = useServices();
  const { goToAppointmentDetails } = useAppNavigation();
  const pages = transactions.data?.pages ?? [];
  const items = pages.flatMap((page) => page.transactions);
  const timeZone = pages[0]?.timezone ?? "UTC";
  // Wait for Froppio data too, so rows don't jump from card details to customer names.
  const busy = transactions.isLoading || appointments.isLoading || clients.isLoading || services.isLoading;

  const renderItem = (transaction: StripeTransaction) => {
    const appointment = transaction.appointmentId
      ? appointments.data?.find((candidate) => candidate.id === transaction.appointmentId && candidate.businessId === businessId)
      : undefined;
    const client = appointment && clients.data?.find((customer) => customer.id === appointment.clientId && customer.businessId === businessId);
    const clientName = client ? `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() || "Unnamed customer" : null;
    const status = getTransactionStatus(transaction);
    const date = formatBusinessDate(transaction.created, timeZone, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const refunded = transaction.amountRefundedMinor > 0 && !transaction.refunded
      ? ` · ${formatStripeMinorAmount(transaction.amountRefundedMinor, transaction.currency)} refunded`
      : "";
    const card = transaction.card ? `${formatCardBrand(transaction.card.brand)} ••${transaction.card.last4}` : "Card payment";

    return (
      <InfoRow
        icon={client
          ? <InitialsAvatar firstName={client.firstName ?? ""} lastName={client.lastName ?? ""} />
          : <span className="flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground"><CreditCard aria-hidden="true" className="size-5" /></span>}
        title={clientName ?? (appointment ? "Customer unavailable" : "Card payment")}
        subtitle={appointment ? getServiceName(appointment, services.data) || "No service assigned" : card}
        caption={`${date}${refunded}`}
        trailing={<span className="flex flex-col items-end gap-1">
          <span className="font-bold tabular-nums">{formatStripeMinorAmount(transaction.amountMinor, transaction.currency)}</span>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </span>}
        onClick={appointment ? () => goToAppointmentDetails(appointment.id, false) : undefined}
        telemetryLabel={appointment ? "Stripe transaction appointment" : undefined}
      />
    );
  };

  return (
    <Section title="Transactions" count="From Stripe">
      <div className="space-y-4">
        <Tabs label="Transaction filter" value={filter} onValueChange={setFilter} options={[
          { value: "all", label: "All" },
          { value: "refunded", label: "Refunded" },
          { value: "disputed", label: "Disputed" },
        ]} />
        {transactions.error && !transactions.isFetchNextPageError ? (
          <ErrorState message={transactions.error.message} onRetry={() => void transactions.refetch()} />
        ) : busy ? (
          <RowSkeleton />
        ) : items.length === 0 ? (
          <EmptyState message={emptyMessages[filter]} />
        ) : (
          <>
            <ItemList divided items={items} renderItem={renderItem} />
            {transactions.isFetchNextPageError && transactions.error && (
              <ErrorState message={transactions.error.message} onRetry={() => void transactions.fetchNextPage()} />
            )}
            {transactions.hasNextPage && !transactions.isFetchNextPageError && (
              <Button variant="outline" className="w-full" disabled={transactions.isFetchingNextPage} onClick={() => void transactions.fetchNextPage()}>
                {transactions.isFetchingNextPage ? "Loading…" : "Show more"}
              </Button>
            )}
          </>
        )}
      </div>
    </Section>
  );
}
